/**
 * Dependencies:
 * - Custom select component
 *
 * Required translation strings:
 * - addToCart
 * - noStock
 * - noVariant
 * - onlyXLeft
 */

if (!customElements.get('variant-picker')) {
  class VariantPicker extends HTMLElement {
    constructor() {
      super();
      this.section = this.closest('.js-product');
      this.productForm = this.section.querySelector('.js-product-form');
      this.optionSelectors = this.querySelectorAll('.option-selector:not(.option-selector--custom)');
      this.nativeSelector = document.getElementById(`variants-${this.section.dataset.section}`);
      this.data = this.getVariantData();
      this.selectedOptions = this.getSelectedOptions();
      this.variant = this.getSelectedVariant();

      this.updateAvailability();
      this.updateAddToCartButton();
      this.updateLegend();
      this.addEventListener('change', this.handleVariantChange.bind(this));

      this.setAttribute('loaded', '');
    }

    handleVariantChange(evt) {
      this.selectedOptions = this.getSelectedOptions();
      this.variant = this.getSelectedVariant();

      // If the combination we just landed on doesn't exist or is sold out,
      // try to auto-correct the OTHER option(s) to the closest combination
      // that is actually available, while keeping the option the shopper
      // just clicked.
      const changedSelector = evt.target.closest('.option-selector');
      const changedIndex = Array.from(this.optionSelectors).indexOf(changedSelector);

      if (changedIndex > -1 && (!this.variant || !this.variant.available)) {
        const changedValue = this.selectedOptions[changedIndex];
        const previousOptions = this.selectedOptions;

        const candidates = this.data.variants.filter(
          (v) => v.options[changedIndex] === changedValue && v.available
        );

        if (candidates.length) {
          let best = candidates[0];
          let bestScore = -1;

          candidates.forEach((candidate) => {
            let score = 0;
            candidate.options.forEach((option, index) => {
              if (index !== changedIndex && option === previousOptions[index]) {
                score += 1;
              }
            });
            if (score > bestScore) {
              bestScore = score;
              best = candidate;
            }
          });

          this.applyVariantSelection(best, changedIndex);
          this.selectedOptions = this.getSelectedOptions();
          this.variant = this.getSelectedVariant();
        }
      }

      this.preSelection = !this.variant && this.selectedOptions.find((o) => o === null) === null;

      this.updateUrl(evt);
      this.updateVariantInput();
      this.updateAddToCartButton();
      this.updateAvailability();
      this.updatePrice();
      this.updateBackorderText();
      this.updatePickupAvailability();
      this.updateSku();
      this.updateBarcode();
      this.updateLabel();
      this.updateLegend();
      VariantPicker.updateLabelText(evt);

      this.dispatchEvent(new CustomEvent('on:variant:change', {
        bubbles: true,
        detail: {
          form: this.productForm,
          variant: this.variant,
          allVariants: this.data.variants,
          selectedOptions: this.selectedOptions
        }
      }));
    }

    applyVariantSelection(variant, skipIndex) {
      this.optionSelectors.forEach((selector, index) => {
        if (index === skipIndex) return;

        const value = variant.options[index];

        if (selector.dataset.selectorType === 'dropdown') {
          const select = selector.querySelector('select');
          if (select) {
            select.value = value;
          }
        } else {
          const input = Array.from(selector.querySelectorAll('input[type="radio"]')).find(
            (el) => el.value === value
          );
          if (input) {
            input.checked = true;
          }
        }
      });
    }

    updateAddToCartButton() {
      if (!this.productForm) return;
      if (this.selectedOptions.indexOf(null) > -1) return;

      this.addBtn = this.addBtn || this.productForm.querySelector('[name="add"]');
      const variantAvailable = this.variant && this.variant.available;
      const unavailableStr = this.variant ? theme.strings.noStock : theme.strings.noVariant;

      this.addBtn.disabled = !variantAvailable;
      this.addBtn.textContent = variantAvailable
        ? this.addBtn.dataset.addToCartText
        : unavailableStr;
    }

    static updateOptionAvailability(optionEl, exists, available) {
      const el = optionEl;
      const isCustomSelectOption = el.classList.contains('custom-select__option');
      // For radio/listed options the clickable, visible element is the <label>
      // right after the (visually-hidden) <input>; for dropdown options the
      // <li class="custom-select__option"> itself is what's rendered.
      const hideTarget = isCustomSelectOption ? el : el.nextElementSibling;

      el.classList.toggle('is-unavailable', !available);
      el.classList.toggle('is-nonexistent', !exists);

      // Hide the option completely rather than just marking it unavailable,
      // so shoppers only ever see choices that actually result in an
      // in-stock variant for their current selection.
      if (el.tagName === 'INPUT') {
        el.disabled = !available;
      }
      if (hideTarget) {
        hideTarget.style.display = available ? '' : 'none';
      }
    }

    updateAvailability() {
      if (this.dataset.showAvailability === 'false') return;
      const { availabilityMode } = this.dataset;
      let currVariant = this.variant;

      if (!this.variant) {
        currVariant = { options: this.selectedOptions };
      }

      if (availabilityMode === 'selection') {
        this.querySelectorAll('.js-option').forEach((optionEl) => {
          VariantPicker.updateOptionAvailability(optionEl, false, false);
        });

        this.optionSelectors.forEach((selector, selectorIndex) => {
          this.data.variants.forEach((variant) => {
            let matchCount = 0;

            variant.options.forEach((option, optionIndex) => {
              if (option === currVariant.options[optionIndex] && optionIndex !== selectorIndex) {
                matchCount += 1;
              }
            });

            if (matchCount === currVariant.options.length - 1) {
              const options = selector.querySelectorAll('.js-option');
              const optionEl = Array.from(options).find((opt) => {
                if (selector.dataset.selectorType === 'dropdown') {
                  return opt.dataset.value === variant.options[selectorIndex];
                }
                return opt.value === variant.options[selectorIndex];
              });

              if (optionEl) {
                VariantPicker.updateOptionAvailability(optionEl, true, variant.available);
              }
            }
          });
        });
      } else {
        this.optionSelectors.forEach((selector, selectorIndex) => {
          const options = selector.querySelectorAll('.js-option:not([data-value=""])');
          options.forEach((option) => {
            const optionValue = selector.dataset.selectorType === 'dropdown' ? option.dataset.value : option.value;
            let variantsExist = false;
            let variantsAvailable = false;
            this.data.variants.forEach((v) => {
              let matches = 0;
              for (let i = 0; i < selectorIndex; i += 1) {
                if (v.options[i] === this.selectedOptions[i] || this.selectedOptions[i] === null) {
                  matches += 1;
                }
              }
              if (v.options[selectorIndex] === optionValue && matches === selectorIndex) {
                variantsExist = true;
                if (v.available) {
                  variantsAvailable = true;
                }
              }
            });
            VariantPicker.updateOptionAvailability(option, variantsExist, variantsAvailable);
          });
        });
      }
    }

    updateBackorderText() {
      this.backorder = this.backorder || this.section.querySelector('.backorder');
      if (!this.backorder) return;

      let hideBackorder = true;

      if (this.variant && this.variant.available) {
        const { inventory } = this.data.formatted[this.variant.id];

        if (this.variant.inventory_management && inventory === 'none') {
          const backorderProdEl = this.backorder.querySelector('.backorder__product');
          const prodTitleEl = this.section.querySelector('.product-title');
          const variantTitle = this.variant.title.includes('Default') ? '' : ` - ${this.variant.title}`;

          backorderProdEl.textContent = `${prodTitleEl.textContent}${variantTitle}`;
          hideBackorder = false;
        }
      }

      this.backorder.hidden = hideBackorder;
    }

    static updateLabelText(evt) {
      const selector = evt.target.closest('.option-selector');
      if (selector.dataset.selectorType === 'dropdown') return;

      const colorText = selector.querySelector('.js-color-text');
      if (!colorText) return;

      colorText.textContent = evt.target.nextElementSibling.querySelector('.js-value').textContent;
    }

    updatePickupAvailability() {
      this.pickUpAvailability = this.pickUpAvailability || this.section.querySelector('pickup-availability');
      if (!this.pickUpAvailability) return;

      if (this.variant && this.variant.available) {
        this.pickUpAvailability.getAvailability(this.variant.id);
      } else {
        this.pickUpAvailability.removeAttribute('available');
        this.pickUpAvailability.innerHTML = '';
      }
    }


    updatePrice() {
      this.price = this.price || this.section.querySelector('.product-price .price');
      if (!this.price) return;

      let { variant } = this;
      if (this.preSelection) {
        variant = this.data.variants[0];
        for (let i = 1; i < this.data.variants.length; i += 1) {
          if (this.data.variants[i].price < variant.price) variant = this.data.variants[i];
        }
      }

      if (variant) {
        const priceCurrentEl = this.price.querySelector('.price__current');
        const priceWasEl = this.price.querySelector('.price__was');
        const unitPriceEl = this.price.querySelector('.unit-price');

        // === Black Friday logic ===
        const rawPrice = this.data.formatted[variant.id].price;
          const numericPrice = (() => {
            let clean = rawPrice.replace(/[^0-9.,]/g, '');

            // CASE 1 — European format: "1.234,56"
            if (clean.match(/,\d{2}$/)) {
              clean = clean.replace(/\./g, '').replace(',', '.');
            }
            // CASE 2 — US/Intl format: "1,234.56"
            else if (clean.match(/\.\d{2}$/)) {
              clean = clean.replace(/,/g, '');
            }

            return parseFloat(clean);
          })();



        // Show the original price as "was"
        if (priceWasEl) {
          priceWasEl.innerHTML = rawPrice;
          priceWasEl.parentElement.classList.remove('hidden');
        }

        // === Labor Day Sale (non-USD) ===
        const laborDayWasEl = this.price.querySelector('.discount_price__non-usd-sale');
        if (laborDayWasEl && !Number.isNaN(numericPrice)) {
          const discounted = (numericPrice * 0.6).toFixed(2);
          const isEuropeanFormat = /,\d{2}$/.test(rawPrice.replace(/[^0-9.,]/g, ''));
          const formattedDiscounted = isEuropeanFormat ? discounted.replace('.', ',') : discounted;
          laborDayWasEl.innerHTML = rawPrice.replace(/[0-9][0-9.,]*[0-9]/, formattedDiscounted);
        }

        // Update price per unit if applicable
        if (unitPriceEl) {
          const baseUnit = this.data.formatted[variant.id].unitPriceBaseUnit;
          unitPriceEl.parentElement.classList.remove('hidden');
          unitPriceEl.innerHTML = `${this.data.formatted[variant.id].unitPrice} / <span class="unit-price__base-unit">${baseUnit}</span>`;
        }
      }
    }

    updateSku() {
      this.sku = this.sku || this.section.querySelector('.product-sku');
      if (!this.sku) return;

      if (this.variant && this.variant.sku) {
        this.sku.querySelector('.sku').textContent = this.variant.sku;
        this.sku.hidden = false;
      } else {
        this.sku.hidden = true;
      }
    }

    updateBarcode() {
      this.barcode = this.barcode || this.section.querySelector('.product-barcode');
      if (!this.barcode) return;

      if (this.variant && this.variant.barcode) {
        this.barcode.querySelector('.barcode').textContent = this.variant.barcode;
        this.barcode.hidden = false;
      } else {
        this.barcode.hidden = true;
      }
    }

    updateVariantInput() {
      this.variantIdInput = this.variantIdInput || this.productForm.querySelector('input[name="id"]');

      if (this.variantIdInput) {
        this.variantIdInput.value = this.variant ? this.variant.id : '';
        this.variantIdInput.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }

    updateUrl(evt) {
      if (!this.variant || this.dataset.updateUrl === 'false') return;

      let newUrl = `${this.dataset.url}?variant=${this.variant.id}`;
      const { search } = window.location;

      if (search.length) {
        const params = new URLSearchParams(search.slice(1));
        params.delete('variant');
        newUrl = `${newUrl}&${params.toString()}`;
      }

      window.history.replaceState({ path: newUrl }, '', newUrl);
    }

    updateLabel() {
      const label = this.querySelector('.variant-picker__label');
      if (label) {
        label.textContent = this.variant ? this.variant.title : theme.strings.unavailable;
      }
    }

    updateLegend() {
      this.optionSelectors.forEach((selector, index) => {
        const legend = selector.querySelector('.label');
        if (legend && this.selectedOptions[index]) {
          legend.textContent = this.selectedOptions[index];
        }
      });
    }

    getSelectedOptions() {
      const options = [];
      this.optionSelectors.forEach((el) => {
        if (el.dataset.selectorType === 'dropdown') {
          options.push(el.querySelector('select').value);
        } else {
          const checkedOption = el.querySelector('input[type="radio"]:checked');
          options.push(checkedOption ? checkedOption.value : null);
        }
      });
      return options;
    }

    getSelectedVariant() {
      const selectedVariant = this.data.variants.find((variant) => {
        return !variant.options.some((option, index) => {
          return this.selectedOptions[index] !== option;
        });
      });
      return selectedVariant || null;
    }

    getVariantData() {
      return JSON.parse(this.querySelector('[type="application/json"]').textContent);
    }
  }

  customElements.define('variant-picker', VariantPicker);
}
